import moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import { RangeModifier } from "react-day-picker";

import { Icon } from "../Icon";
import { Text } from "../Text";
import { ActionsPopOut } from "../ActionsPopOut";
import { DayRangePicker } from "../DayRangePicker";
import { Icons } from "../Icon/types";

interface DateRangePickerCompactProps extends Pick<React.HTMLAttributes<HTMLDivElement>, "className"> {
  onDateChange: (range: RangeModifier) => void;
  maxDate: Date;
  minDate: Date;
  date?: Date;
  selectDatesTitle?: string;
  dateFormat?: string;
  disableConfirmAction?: boolean;
}

/**
 * Date range picker compact
 */
const DateRangePickerCompact: React.ReactFCWithChildren<DateRangePickerCompactProps> = ({
  onDateChange,
  maxDate,
  minDate,
  date,
  selectDatesTitle,
  dateFormat,
  disableConfirmAction, // Defaults to false
  ...rest
}) => {
  const divRef = React.useRef<HTMLDivElement>(null);
  const [ openDateRangePickerPopout, setOpenDateRangePickerPopout ] = React.useState(false);
  const [ selectedFromDate, setSelectedFromDate ] = React.useState<Date>();
  const [ selectedToDate, setSelectedToDate ] = React.useState<Date>();
  const parsedDateFormat = React.useMemo(() => (dateFormat || "ddd DD MMM YYYY"), [ dateFormat ]);

  /**
   * Set the date text according to the selected from and to dates (if any)
   */
  const dateText = React.useMemo(() => {
    // If there are no dates select
    if (!selectedFromDate && !selectedToDate) {
      return selectDatesTitle;
    }

    // If ONLY the "from" date is set
    if (selectedFromDate && !selectedToDate) {
      return `${moment.utc(selectedFromDate).format(parsedDateFormat)} - `;
    }

    // If ONLY the "to" date is set
    if (!selectedFromDate && selectedToDate) {
      return ` - ${moment.utc(selectedToDate).format(parsedDateFormat)}`;
    }

    return `${moment.utc(selectedFromDate).format(parsedDateFormat)} - ${moment.utc(selectedToDate).format(parsedDateFormat)}`;
  }, [
    selectDatesTitle,
    parsedDateFormat,
    selectedFromDate,
    selectedToDate
  ]);

  /**
   * Handle date change
   */
  const handleDateChange = React.useCallback(({ from, to }: RangeModifier) => {
    // If the from value exists, update the selectedFromDate state
    if (from) {
      setSelectedFromDate(from);
    }

    // If the from value exists, update the selectedToDate state
    if (to) {
      setSelectedToDate(to);
    }

    // Call the given onDateChange function
    onDateChange({
      from,
      to
    });
  }, [ onDateChange ]);

  return (
    <StyledDayRangePickerPopoutButton {...rest}>
      <div
        className="dates"
        onClick={() => setOpenDateRangePickerPopout(true)}
      >
        <Icon
          size={18}
          icon={Icons.CALENDAR}
          $fillColor="midGrey"
        />

        <Text>
          {dateText}
        </Text>
      </div>

      {openDateRangePickerPopout && (
        <ActionsPopOut
          ref={divRef}
          directionHorizontal="left"
          directionVertical="bottom"
          action={() => setOpenDateRangePickerPopout(false)}
          handleClose={() => setOpenDateRangePickerPopout(false)}
          disableAction={disableConfirmAction}
          steps={[
            {
              title: selectDatesTitle,
              allowNext: false,
              component: (
                <DayRangePicker
                  maxDate={maxDate}
                  date={(date) || new Date()}
                  minDate={minDate}
                  handleDateChange={handleDateChange}
                />
              )
            }
          ]}
        />
      )}
    </StyledDayRangePickerPopoutButton>
  );
};

// Set the default props
DateRangePickerCompact.defaultProps = {
  selectDatesTitle: "Select dates",
  disableConfirmAction: false
};

export default DateRangePickerCompact;

const StyledDayRangePickerPopoutButton = styled.div`
  position: relative;
  width: 100%;
  max-width: 40rem;

  .dates {
    display: flex;
    flex-direction: row;
    cursor: pointer;

    > span {
      ${({ theme }) => css`
        margin-left: ${theme.spacing.padding.xs};
      `}
    }
  }
`;
